import React, { FC } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableFooter,
} from '@mui/material';
import { useUnit } from 'effector-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  $exportTasks,
  $exportTasksTotal,
  $listPending,
  $exportTasksPage,
  $exportTasksPageSize,
  setExportTasksPage,
  setExportTasksPageSize,
  EXPORT_TASKS_PAGE_SIZE_OPTIONS,
} from './model';

export const DynamicObjectExportTaskList: FC = () => {
  const [
    exportTasks,
    exportTasksTotal,
    listPending,
    page,
    rowsPerPage,
  ] = useUnit([
    $exportTasks,
    $exportTasksTotal,
    $listPending,
    $exportTasksPage,
    $exportTasksPageSize,
  ]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setExportTasksPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setExportTasksPageSize(value);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="h6">Список задач экспорта</Typography>
      <Typography variant="body2" color="text.secondary">
        Всего задач: {exportTasksTotal}
        {listPending ? ' (обновляем…)' : ''}
      </Typography>

      {listPending && exportTasks.length === 0 && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">Загрузка списка задач…</Typography>
        </Box>
      )}

      {!listPending && exportTasks.length === 0 && exportTasksTotal === 0 && (
        <Typography variant="body2" color="text.secondary">
          Задачи экспорта ещё не создавались.
        </Typography>
      )}

      {(exportTasks.length > 0 || exportTasksTotal > 0) && (
        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Отчёт</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Ошибка</TableCell>
                <TableCell>Создана</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exportTasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>{task.id}</TableCell>
                  <TableCell>{task.name}</TableCell>
                  <TableCell>
                    {task.output ? (
                      <a
                        href={task.output as string}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Скачать
                      </a>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{task.status}</TableCell>
                  <TableCell>{task.error ?? '—'}</TableCell>
                  <TableCell>
                    {task.createdAt
                      ? format(new Date(task.createdAt), 'dd.MM.yyyy', {
                          locale: ru,
                        })
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={6} sx={{ borderBottom: 0 }}>
                  <TablePagination
                    component="div"
                    count={exportTasksTotal}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[...EXPORT_TASKS_PAGE_SIZE_OPTIONS]}
                    labelRowsPerPage="Строк на странице:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`
                    }
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

